// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	BackgroundProcessor,
	type BackgroundProcessorOptions as LiveKitBackgroundProcessorOptions,
} from '@livekit/track-processors';
import type {Track, TrackProcessor, VideoProcessorOptions} from 'livekit-client';

const logger = new Logger('CameraVideoProcessor');
const DEFAULT_FALLBACK_WIDTH = 640;
const DEFAULT_FALLBACK_HEIGHT = 360;
const DEFAULT_FALLBACK_FRAME_RATE = 30;

type VideoTrackProcessor = TrackProcessor<Track.Kind.Video, VideoProcessorOptions>;
type MediaStreamTrackProcessorInstance = {
	readable: ReadableStream<VideoFrame>;
};
type MediaStreamTrackProcessorConstructor = new (options: {
	track: MediaStreamTrack;
}) => MediaStreamTrackProcessorInstance;
type MediaStreamTrackGeneratorInstance = MediaStreamTrack & {
	writable: WritableStream<VideoFrame>;
};
type MediaStreamTrackGeneratorConstructor = new (options: {
	kind: 'video';
	signalTarget?: MediaStreamTrack;
}) => MediaStreamTrackGeneratorInstance;
type Canvas2DContext = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

interface MediaStreamTrackProcessorGlobals {
	MediaStreamTrackProcessor?: MediaStreamTrackProcessorConstructor;
	MediaStreamTrackGenerator?: MediaStreamTrackGeneratorConstructor;
}

export interface CameraVideoProcessorOptions {
	backgroundOptions?: LiveKitBackgroundProcessorOptions;
	mirror?: boolean;
}

function getTrackProcessorGlobals(): MediaStreamTrackProcessorGlobals {
	return globalThis as unknown as MediaStreamTrackProcessorGlobals;
}

function createCanvas(width: number, height: number): OffscreenCanvas | HTMLCanvasElement {
	if (typeof OffscreenCanvas !== 'undefined') {
		return new OffscreenCanvas(width, height);
	}
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	return canvas;
}

function getCanvasContext(canvas: OffscreenCanvas | HTMLCanvasElement): Canvas2DContext | null {
	return canvas.getContext('2d', {alpha: false}) as Canvas2DContext | null;
}

function resizeCanvas(canvas: OffscreenCanvas | HTMLCanvasElement, width: number, height: number): void {
	if (canvas.width === width && canvas.height === height) {
		return;
	}
	canvas.width = width;
	canvas.height = height;
}

class MirrorVideoProcessor implements VideoTrackProcessor {
	name = 'camera-mirror-processor';
	processedTrack?: MediaStreamTrack;
	private canvas: OffscreenCanvas | HTMLCanvasElement | null = null;
	private canvasContext: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null = null;
	private abortController: AbortController | null = null;
	private pipePromise: Promise<void> | null = null;
	private fallbackVideoElement: HTMLVideoElement | null = null;
	private fallbackStream: MediaStream | null = null;
	private fallbackAnimationFrameId: number | null = null;
	private fallbackLastFrameAt = 0;

	async init(opts: VideoProcessorOptions): Promise<void> {
		await this.setup(opts.track);
	}

	async restart(opts: VideoProcessorOptions): Promise<void> {
		await this.destroy();
		await this.setup(opts.track);
	}

	async destroy(): Promise<void> {
		this.abortController?.abort();
		this.abortController = null;
		if (this.fallbackAnimationFrameId != null) {
			cancelAnimationFrame(this.fallbackAnimationFrameId);
			this.fallbackAnimationFrameId = null;
		}
		if (this.fallbackVideoElement) {
			this.fallbackVideoElement.pause();
			this.fallbackVideoElement.srcObject = null;
			this.fallbackVideoElement.remove();
			this.fallbackVideoElement = null;
		}
		this.fallbackStream?.getTracks().forEach((track) => track.stop());
		this.fallbackStream = null;
		this.processedTrack?.stop();
		this.processedTrack = undefined;
		this.canvas = null;
		this.canvasContext = null;
		await this.pipePromise?.catch(() => {});
		this.pipePromise = null;
	}

	private async setup(sourceTrack: MediaStreamTrack): Promise<void> {
		const globals = getTrackProcessorGlobals();
		if (globals.MediaStreamTrackProcessor && globals.MediaStreamTrackGenerator) {
			this.setupStreamProcessor(sourceTrack, globals.MediaStreamTrackProcessor, globals.MediaStreamTrackGenerator);
			return;
		}
		this.setupCanvasFallback(sourceTrack);
	}

	private setupStreamProcessor(
		sourceTrack: MediaStreamTrack,
		Processor: MediaStreamTrackProcessorConstructor,
		Generator: MediaStreamTrackGeneratorConstructor,
	): void {
		const sourceSettings = sourceTrack.getSettings();
		const initialWidth = sourceSettings.width ?? DEFAULT_FALLBACK_WIDTH;
		const initialHeight = sourceSettings.height ?? DEFAULT_FALLBACK_HEIGHT;
		const canvas = createCanvas(initialWidth, initialHeight);
		const canvasContext = getCanvasContext(canvas);
		if (!canvasContext) {
			throw new Error('Unable to create camera mirror canvas context');
		}
		this.canvas = canvas;
		this.canvasContext = canvasContext;
		const processor = new Processor({track: sourceTrack});
		const generator = new Generator({kind: 'video', signalTarget: sourceTrack});
		const transform = new TransformStream<VideoFrame, VideoFrame>({
			transform: (frame, controller) => this.mirrorFrame(frame, controller),
		});
		this.abortController = new AbortController();
		this.pipePromise = processor.readable
			.pipeThrough(transform)
			.pipeTo(generator.writable, {signal: this.abortController.signal})
			.catch((error) => {
				if (error instanceof DOMException && error.name === 'AbortError') {
					return;
				}
				logger.warn('Camera mirror stream processor failed', {error});
			});
		this.processedTrack = generator;
	}

	private mirrorFrame(frame: VideoFrame, controller: TransformStreamDefaultController<VideoFrame>): void {
		let enqueuedOriginalFrame = false;
		try {
			const width = frame.displayWidth || frame.codedWidth;
			const height = frame.displayHeight || frame.codedHeight;
			if (!this.canvas || !this.canvasContext || width <= 0 || height <= 0) {
				controller.enqueue(frame);
				enqueuedOriginalFrame = true;
				return;
			}
			resizeCanvas(this.canvas, width, height);
			this.drawMirrored(frame, width, height);
			controller.enqueue(
				new VideoFrame(this.canvas, {
					timestamp: frame.timestamp ?? Math.round(performance.now() * 1000),
				}),
			);
		} catch (error) {
			logger.warn('Failed to mirror camera frame', {error});
			if (!enqueuedOriginalFrame) {
				controller.enqueue(frame);
				enqueuedOriginalFrame = true;
			}
		} finally {
			if (!enqueuedOriginalFrame) {
				frame.close();
			}
		}
	}

	private drawMirrored(source: CanvasImageSource, width: number, height: number): void {
		const ctx = this.canvasContext;
		if (!ctx) {
			return;
		}
		ctx.save();
		ctx.clearRect(0, 0, width, height);
		ctx.translate(width, 0);
		ctx.scale(-1, 1);
		ctx.drawImage(source, 0, 0, width, height);
		ctx.restore();
	}

	private setupCanvasFallback(sourceTrack: MediaStreamTrack): void {
		if (typeof HTMLCanvasElement === 'undefined' || !('captureStream' in HTMLCanvasElement.prototype)) {
			throw new Error('Camera mirror processing is not supported in this browser');
		}
		const sourceSettings = sourceTrack.getSettings();
		const width = sourceSettings.width ?? DEFAULT_FALLBACK_WIDTH;
		const height = sourceSettings.height ?? DEFAULT_FALLBACK_HEIGHT;
		const frameRate = sourceSettings.frameRate ?? DEFAULT_FALLBACK_FRAME_RATE;
		const videoElement = document.createElement('video');
		videoElement.muted = true;
		videoElement.autoplay = true;
		videoElement.playsInline = true;
		videoElement.srcObject = new MediaStream([sourceTrack]);
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const canvasContext = getCanvasContext(canvas);
		if (!canvasContext) {
			throw new Error('Unable to create camera mirror fallback canvas context');
		}
		this.fallbackVideoElement = videoElement;
		this.canvas = canvas;
		this.canvasContext = canvasContext;
		this.fallbackStream = canvas.captureStream(frameRate);
		this.processedTrack = this.fallbackStream.getVideoTracks()[0];
		videoElement.play().catch((error) => {
			logger.warn('Failed to start camera mirror fallback video element', {error});
		});
		const minFrameInterval = 1000 / Math.max(1, frameRate);
		const drawFrame = (now: number) => {
			if (!this.fallbackVideoElement || !this.canvas) {
				return;
			}
			if (now - this.fallbackLastFrameAt >= minFrameInterval) {
				this.fallbackLastFrameAt = now;
				const nextWidth = this.fallbackVideoElement.videoWidth || width;
				const nextHeight = this.fallbackVideoElement.videoHeight || height;
				resizeCanvas(this.canvas, nextWidth, nextHeight);
				if (this.fallbackVideoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
					this.drawMirrored(this.fallbackVideoElement, nextWidth, nextHeight);
				}
			}
			this.fallbackAnimationFrameId = requestAnimationFrame(drawFrame);
		};
		this.fallbackAnimationFrameId = requestAnimationFrame(drawFrame);
	}
}

class CameraVideoProcessor implements VideoTrackProcessor {
	name = 'camera-video-processor';
	processedTrack?: MediaStreamTrack;
	private backgroundProcessor: VideoTrackProcessor | null = null;
	private mirrorProcessor: MirrorVideoProcessor | null = null;

	constructor(private readonly options: CameraVideoProcessorOptions) {}

	async init(opts: VideoProcessorOptions): Promise<void> {
		await this.setup(opts);
	}

	async restart(opts: VideoProcessorOptions): Promise<void> {
		await this.destroy();
		await this.setup(opts);
	}

	async destroy(): Promise<void> {
		const mirrorProcessor = this.mirrorProcessor;
		const backgroundProcessor = this.backgroundProcessor;
		this.mirrorProcessor = null;
		this.backgroundProcessor = null;
		this.processedTrack = undefined;
		await mirrorProcessor?.destroy();
		await backgroundProcessor?.destroy();
	}

	private async setup(opts: VideoProcessorOptions): Promise<void> {
		let inputTrack = opts.track;
		if (this.options.backgroundOptions) {
			const backgroundProcessor = BackgroundProcessor(this.options.backgroundOptions) as VideoTrackProcessor;
			this.backgroundProcessor = backgroundProcessor;
			await backgroundProcessor.init(opts);
			inputTrack = backgroundProcessor.processedTrack ?? inputTrack;
		}
		if (this.options.mirror) {
			const mirrorProcessor = new MirrorVideoProcessor();
			this.mirrorProcessor = mirrorProcessor;
			await mirrorProcessor.init({...opts, track: inputTrack});
			inputTrack = mirrorProcessor.processedTrack ?? inputTrack;
		}
		this.processedTrack = inputTrack === opts.track ? undefined : inputTrack;
	}
}

export function createCameraVideoProcessor(options: CameraVideoProcessorOptions): VideoTrackProcessor {
	return new CameraVideoProcessor(options);
}
